import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, fecha, hoy } from '../utils/formato';
import { creditosPendientes } from '../utils/creditos';
import RelacionVenta from './RelacionVenta';
import { periodoDe, DIAS, DIAS_DE_QUINCENA, cortesDeQuincena,
         FRECUENCIAS } from '../utils/periodo';

// Quién le debe, y anotar los abonos.
//
// Es el estado de cuenta de las fincas al derecho. Aquí tampoco se guarda un
// saldo: se suma lo comprado a crédito menos lo abonado, cada vez que se pide.
function Cartera() {
  const [cartera, setCartera] = useState([]);
  const [creditos, setCreditos] = useState({ ventas: [], abonos: [] });
  const [falloCreditos, setFalloCreditos] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [abonando, setAbonando] = useState(null);
  const [nuevo, setNuevo] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [relacion, setRelacion] = useState(null);

  // La cuenta del cliente en un periodo, para imprimírsela.
  //
  // Se abre en el mes que corre, pero casi siempre se saca el que acaba de
  // cerrar; para eso están las flechas de la hoja. El periodo se pide al
  // servidor y no se arma aquí: la cuenta que se cobra tiene que salir del
  // mismo sitio de donde sale la de 💸 Por cobrar.
  const abrirRelacion = async (cli, periodo) => {
    // En SU periodo, no en el mes. Al cliente al que se le factura cada quince
    // días había que corregirle las dos fechas a mano cada vez que abría la
    // hoja, y el día que se le olvide le cobra el periodo que no es.
    const p = periodo || periodoDe(cli.frecuencia_pago || 'mensual', hoy(),
                                   cli.semana_empieza, cli.quincena_empieza,
                                   cli.ciclo_desde);
    try {
      const { data } = await axios.get(
        `/api/clientes/${cli.id}/relacion?desde=${p.desde}&hasta=${p.hasta}`);
      setRelacion(data);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo armar la relación');
    }
  };

  const cargar = useCallback(() => {
    setCargando(true);
    axios.get('/api/clientes/cartera')
      .then(r => setCartera(r.data || []))
      .catch(() => setError('No se pudo cargar la cartera'))
      .finally(() => setCargando(false));
    axios.get('/api/clientes/creditos')
      .then(r => { setCreditos(r.data || { ventas: [], abonos: [] }); setFalloCreditos(false); })
      .catch(() => setFalloCreditos(true));
  }, []);

  useEffect(cargar, [cargar]);

  // Crear y corregir por la MISMA puerta.
  //
  // Es el mismo formulario con los mismos campos; lo único que cambia es si
  // lleva `id`. Dos formularios separados para lo mismo terminan pidiendo
  // cosas distintas —a uno se le agrega un campo y al otro se le olvida— y
  // entonces corregir una ficha le borra datos que solo estaban en el de
  // crear.
  const guardarCliente = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const datos = {
        ...nuevo,
        descuento_kilos: parseFloat(nuevo.descuento_kilos) || 0,
        frecuencia_pago: nuevo.frecuencia_pago || 'mensual',
        semana_empieza: nuevo.semana_empieza ?? 1,
        quincena_empieza: nuevo.quincena_empieza ?? 1,
        ciclo_desde: nuevo.ciclo_desde || null,
      };
      if (nuevo.id) await axios.put(`/api/clientes/${nuevo.id}`, datos);
      else await axios.post('/api/clientes', datos);
      setNuevo(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar');
    }
    setGuardando(false);
  };

  const abonar = async (e) => {
    e.preventDefault();
    const monto = parseFloat(abonando.monto);
    if (!(monto > 0)) return alert('El monto debe ser mayor que cero');

    setGuardando(true);
    try {
      await axios.post('/api/abonos', {
        cliente_id: abonando.cliente.id,
        fecha: abonando.fecha,
        monto,
        forma: abonando.forma,
        nota: abonando.nota || null
      });
      setAbonando(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar el abono');
    }
    setGuardando(false);
  };

  // El reparto vive en `utils/creditos.js` y está probado aparte: es plata, y
  // una cuenta de plata metida dentro de una pantalla no se puede probar.
  const pendientes = creditosPendientes(creditos.ventas, creditos.abonos);

  const deben = cartera.filter(c => c.saldo > 0);
  const total = deben.reduce((s, c) => s + c.saldo, 0);

  // A quién le debe hace más tiempo. Es lo que se mira antes de salir a cobrar.
  const masViejo = deben
    .filter(c => c.ultima_compra)
    .sort((a, b) => new Date(a.ultimo_abono || a.ultima_compra) - new Date(b.ultimo_abono || b.ultima_compra))[0];

  return (
    <>
      <RelacionVenta
        r={relacion} alCerrar={() => setRelacion(null)}
        alCambiarPeriodo={(p) => relacion && abrirRelacion(relacion.cliente, p)} />

      <div className="encabezado">
        {/* Se llamaba «Cartera — quién le debe y desde cuándo», de cuando la
            pantalla solo miraba deudas. Ahora también dice quién le compra, y
            un título que promete menos de lo que hay hace que nadie busque el
            resto. */}
        <h1>Clientes</h1>
        <p>Quién le compra, cuánto, y quién le debe</p>
      </div>

      {error && <div className="aviso malo">{error}</div>}

      <div className="rejilla">
        <div className="dato" style={{ '--color': 'var(--venta)' }}>
          <div className="rotulo">📥 Le deben en total</div>
          <div className="valor cifra">{plata(total)}</div>
          <div className="pie">{deben.length} {deben.length === 1 ? 'cliente' : 'clientes'}</div>
        </div>
        {masViejo && (
          <div className="dato" style={{ '--color': 'var(--ojo)' }}>
            <div className="rotulo">⏳ Lleva más tiempo sin abonar</div>
            <div className="valor" style={{ fontSize: '1.25rem' }}>{masViejo.nombre}</div>
            <div className="pie">
              {plata(masViejo.saldo)} · último movimiento{' '}
              {fecha(masViejo.ultimo_abono || masViejo.ultima_compra)}
            </div>
          </div>
        )}
      </div>

      {/* Los que más compran.
          Es la pregunta que un negocio se hace de verdad —«¿quién me mantiene
          el mes?»— y que la cartera nunca contestaba, porque solo miraba
          deudas. El mejor cliente puede ser justo el que nunca debe nada.

          Se muestran tres, no diez: una lista larga no es un podio, es otra
          tabla que hay que leer entera. */}
      {cartera.filter(c => c.comprado > 0).length > 1 && (
        <div className="bloque">
          <h2>🏆 Los que más compran</h2>
          <div className="cuerpo">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {cartera.filter(c => c.comprado > 0).slice(0, 3).map((c, i) => (
                <div key={c.id} style={{
                  border: '1px solid var(--linea)', borderRadius: 8,
                  padding: '10px 16px', minWidth: 190, flex: '1 1 190px',
                  background: i === 0 ? 'var(--hondo)' : undefined,
                }}>
                  <div style={{ fontSize: 12, color: 'var(--tinta-tenue)' }}>
                    {['🥇 El primero', '🥈 El segundo', '🥉 El tercero'][i]}
                  </div>
                  <div className="recio" style={{ fontSize: 16 }}>{c.nombre}</div>
                  {c.ciudad && (
                    <div style={{ fontSize: 12, color: 'var(--acento)', fontWeight: 600 }}>
                      📍 {c.ciudad}
                    </div>
                  )}
                  <div className="cifra recio" style={{ fontSize: 19, marginTop: 4 }}>
                    {plata(c.comprado)}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--tinta-tenue)' }}>
                    {c.veces} {c.veces === 1 ? 'compra' : 'compras'}
                    {c.kilos > 0 && ` · ${Math.round(c.kilos)} kg`}
                    {/* Que deba no lo saca del podio: compra mucho Y debe. Son
                        dos cosas y las dos hay que verlas juntas. */}
                    {c.saldo > 0 && (
                      <span style={{ color: 'var(--malo)', fontWeight: 600 }}>
                        {' · debe '}{plata(c.saldo)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}


      {falloCreditos && (
        <div className="aviso malo">
          <strong>No se pudo cargar lo fiado.</strong> El listado de despachos
          sin pagar no se está mostrando — no quiere decir que no haya.
          Recargue la página; si sigue igual, avise.
        </div>
      )}

      {/* EL LISTADO DE LO FIADO, DESPACHO POR DESPACHO.
          La tabla de abajo da un saldo por persona, que es lo que se necesita
          para abonar. Pero cobrar no se hace por saldos: se hace por despachos
          —«el del 4 de junio todavía no me lo ha pagado»—, y eso no salía en
          ninguna parte.

          Va del más viejo al más nuevo, sin separar por cliente: uno sale a
          cobrar por lo que más lleva debiendo, sea de quien sea. */}
      {pendientes.length > 0 && (
        <div className="bloque">
          <h2>
            🧾 Lo que está fiado
            <span className="contador">
              {pendientes.length} {pendientes.length === 1 ? 'despacho' : 'despachos'}
              {' · '}{plata(pendientes.reduce((s, v) => s + v.saldo, 0))}
            </span>
          </h2>
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Despacho</th>
                  <th style={{ textAlign: 'right' }}>Venta</th>
                  <th style={{ textAlign: 'right' }}>Debe</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pendientes.map(v => (
                  <tr key={v.id}>
                    <td className="recio">
                      {v.cliente}
                      {(v.ciudad || v.telefono) && (
                        <div style={{ fontSize: 12, fontWeight: 400,
                                      color: 'var(--tinta-tenue)' }}>
                          {[v.ciudad && '📍 ' + v.ciudad, v.telefono]
                            .filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {fecha(v.fecha)}
                      {/* Los días que lleva, que es lo que decide a quién se
                          llama primero. Una fecha sola obliga a restar. */}
                      {v.dias !== null && (
                        <div style={{ fontSize: 12, fontWeight: 700,
                                      color: v.dias > 60 ? 'var(--malo)'
                                           : v.dias > 30 ? 'var(--ojo)'
                                           : 'var(--tinta-tenue)' }}>
                          {v.dias === 0 ? 'hoy'
                            : v.dias === 1 ? 'hace 1 día'
                            : `hace ${v.dias} días`}
                        </div>
                      )}
                    </td>
                    <td className="num" style={{ color: 'var(--tinta-tenue)' }}>
                      {plata(v.total)}
                    </td>
                    <td className="num recio" style={{ color: 'var(--malo)' }}>
                      {plata(v.saldo)}
                      {/* Que haya abonado algo cambia cómo se le cobra: no es
                          lo mismo el que viene pagando que el que no ha dado
                          nada. */}
                      {v.parcial && (
                        <div style={{ fontSize: 11, fontWeight: 400,
                                      color: 'var(--tinta-tenue)' }}>
                          abonó {plata(v.abonado)}
                        </div>
                      )}
                    </td>
                    <td>
                      <button className="boton chico suave"
                              onClick={() => {
                                const c = cartera.find(x => x.id === v.cliente_id);
                                if (!c) return;
                                setAbonando({ cliente: c, fecha: hoy(),
                                              monto: String(Math.round(v.saldo)),
                                              forma: 'efectivo', nota: '' });
                              }}>
                        Abonar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cuerpo" style={{ paddingTop: 10 }}>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--tinta-tenue)' }}>
              Los abonos se le hacen al cliente, no al despacho, así que se
              reparten tapando primero lo más viejo. <strong>Lo que debe cada
              cliente es exacto</strong> — lo que puede discutirse es cuál
              despacho quedó pendiente, no cuánto.
            </p>
          </div>
        </div>
      )}

      <div className="bloque">
        <h2>
          👥 Clientes
          <span className="contador">{cartera.length}</span>
          <button className="boton chico" style={{ marginLeft: 'auto' }}
                  onClick={() => setNuevo({ nombre: '', telefono: '', direccion: '',
                                            ciudad: '', documento: '', tipo: 'mayor',
                                            frecuencia_pago: 'mensual',
                                            semana_empieza: 1, quincena_empieza: 1 })}>
            ➕ Nuevo cliente
          </button>
        </h2>

        {cargando ? (
          <div className="vacio">Cargando…</div>
        ) : cartera.length === 0 ? (
          <div className="vacio">
            Todavía no hay clientes. Agrega el primero para poder venderle a crédito.
          </div>
        ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Tipo</th>
                  <th style={{ textAlign: 'right' }}>Le ha comprado</th>
                  <th style={{ textAlign: 'right' }}>Comprado fiado</th>
                  <th style={{ textAlign: 'right' }}>Abonado</th>
                  <th style={{ textAlign: 'right' }}>Debe</th>
                  <th>Último abono</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cartera.map(c => (
                  <tr key={c.id}>
                    <td className="recio">
                      {c.nombre}
                      {c.ciudad && (
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--acento)' }}>
                          📍 {c.ciudad}
                        </div>
                      )}
                      {(c.telefono || c.direccion) && (
                        <div style={{ fontSize: 12, color: 'var(--tinta-tenue)', fontWeight: 400 }}>
                          {[c.telefono, c.direccion].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="pinta gris">{c.tipo === 'mayor' ? 'Por mayor' : 'Detal'}</span>
                    </td>
                    {/* Lo que le ha comprado EN TOTAL, de contado y fiado.
                        Antes esta columna solo contaba lo fiado y el mejor
                        cliente —el que paga al momento— salía en cero. */}
                    <td className="num recio">
                      {c.comprado > 0 ? plata(c.comprado) : '—'}
                      {c.veces > 0 && (
                        <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                          {c.veces} {c.veces === 1 ? 'vez' : 'veces'}
                          {c.kilos > 0 && ` · ${Math.round(c.kilos)} kg`}
                        </div>
                      )}
                    </td>
                    <td className="num">{c.fiado > 0 ? plata(c.fiado) : '—'}</td>
                    <td className="num">{c.abonado > 0 ? plata(c.abonado) : '—'}</td>
                    {/* Un saldo negativo no es una deuda negativa: es plata a
                        favor del cliente, y pasa al anular una venta que ya se
                        había abonado. Escribirlo como «−$1.000.000 debe» hace
                        pensar en un error del programa. */}
                    <td className="num recio"
                        style={{ color: c.saldo > 0 ? 'var(--malo)'
                               : c.saldo < 0 ? 'var(--leche)' : 'var(--bueno)' }}>
                      {c.saldo < 0
                        ? <>{plata(-c.saldo)}<div style={{ fontSize: 11, fontWeight: 400 }}>a favor</div></>
                        : plata(c.saldo)}
                    </td>
                    <td>{c.ultimo_abono ? fecha(c.ultimo_abono) : '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {c.saldo > 0 && (
                        <button className="boton chico suave"
                                style={{ marginRight: 5 }}
                                onClick={() => setAbonando({
                                  cliente: c, fecha: hoy(),
                                  monto: String(Math.round(c.saldo)),
                                  forma: 'efectivo', nota: ''
                                })}>
                          Abonar
                        </button>
                      )}
                      {/* La cuenta del periodo, para llevársela cuando se va
                          a cobrar. Es la hermana de la relación de la finca:
                          la finca la tenía desde el principio y el cliente
                          cobraba sin ningún papel que enseñar. */}
                      {/* Con la palabra, no solo el icono.
                          
                          En 🚜 Fincas el mismo botón dice «🧾 Relación» y aquí
                          era un 🧾 pelado con la explicación escondida en el
                          globito. Gabriel dijo que las facturas del queso no
                          salían como las de la leche: la hoja existía y estaba
                          bien, lo que no existía era la palabra que la nombra.
                          
                          Un icono solo se entiende cuando ya se sabe qué hace. */}
                      <button className="boton chico suave"
                              style={{ marginRight: 5 }}
                              title="La cuenta del mes de este cliente"
                              onClick={() => abrirRelacion(c)}>
                        🧾 Relación
                      </button>
                      {/* Corregir la ficha. Un teléfono mal tecleado o un
                          pueblo equivocado no se podían arreglar: había que
                          crear el cliente otra vez, y entonces su cartera
                          quedaba partida en dos fichas con el mismo nombre. */}
                      <button className="boton chico suave" title="Corregir la ficha"
                              onClick={() => setNuevo({
                                id: c.id,
                                nombre: c.nombre || '',
                                telefono: c.telefono || '',
                                documento: c.documento || '',
                                ciudad: c.ciudad || '',
                                direccion: c.direccion || '',
                                tipo: c.tipo || 'mayor',
                                descuento_kilos: c.descuento_kilos || '',
                                frecuencia_pago: c.frecuencia_pago || 'mensual',
                                semana_empieza: c.semana_empieza ?? 1,
                                quincena_empieza: c.quincena_empieza ?? 1,
                                ciclo_desde: c.ciclo_desde || '',
                              })}>
                        ✏️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {nuevo && (
        <div className="bloque">
          <h2>{nuevo.id ? `✏️ Corregir a ${nuevo.nombre || 'este cliente'}` : '➕ Nuevo cliente'}</h2>
          <form className="cuerpo" onSubmit={guardarCliente}>
            <div className="campos">
              <label className="campo">
                <span>Nombre *</span>
                <input value={nuevo.nombre} required
                       onChange={e => setNuevo({ ...nuevo, nombre: e.target.value })} />
              </label>
              <label className="campo">
                <span>Teléfono</span>
                <input value={nuevo.telefono}
                       onChange={e => setNuevo({ ...nuevo, telefono: e.target.value })} />
              </label>
              <label className="campo">
                <span>Cédula o NIT</span>
                <input value={nuevo.documento}
                       onChange={e => setNuevo({ ...nuevo, documento: e.target.value })} />
              </label>
              {/* La ciudad va ANTES de la dirección, que es el orden en que se
                  piensa un despacho: primero para qué pueblo, después a qué
                  calle. */}
              <label className="campo">
                <span>Ciudad o pueblo</span>
                <input value={nuevo.ciudad} placeholder="Para dónde se despacha"
                       onChange={e => setNuevo({ ...nuevo, ciudad: e.target.value })} />
              </label>
              <label className="campo">
                <span>Dirección</span>
                <input value={nuevo.direccion}
                       onChange={e => setNuevo({ ...nuevo, direccion: e.target.value })} />
              </label>
              {/* CADA CUÁNTO SE LE FACTURA, igual que a la finca.
                  
                  «La factura sale mensual.» La relación del cliente abría
                  siempre en el mes, mientras la de la finca ya abría en el
                  periodo de cada una. Al cliente al que se le cobra cada quince
                  días había que corregirle las dos fechas a mano cada vez. */}
              <label className="campo">
                <span>Cada cuánto se le factura</span>
                <select value={nuevo.frecuencia_pago || 'mensual'}
                        onChange={e => setNuevo({ ...nuevo,
                          frecuencia_pago: e.target.value })}>
                  {Object.entries(FRECUENCIAS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </label>
              {nuevo.frecuencia_pago === 'semanal' && (
                <label className="campo">
                  <span>¿Qué día empieza la semana?</span>
                  <select value={nuevo.semana_empieza ?? 1}
                          onChange={e => setNuevo({ ...nuevo,
                            semana_empieza: parseInt(e.target.value) })}>
                    {DIAS.map((d, i) => (
                      <option key={i} value={i}>
                        {d.charAt(0).toUpperCase() + d.slice(1)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {nuevo.frecuencia_pago === 'quincenal' && (
                <label className="campo">
                  <span>¿Qué día empieza la quincena?</span>
                  <select value={nuevo.quincena_empieza ?? 1}
                          onChange={e => setNuevo({ ...nuevo,
                            quincena_empieza: parseInt(e.target.value) })}>
                    {/* Los DOS días, como se habla el acuerdo. */}
                    {DIAS_DE_QUINCENA.map(n => {
                      const [a, b] = cortesDeQuincena(n);
                      return (
                        <option key={n} value={n}>
                          Del {a} y del {b}{n === 1 ? ' (lo de siempre)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </label>
              )}
              {nuevo.frecuencia_pago === 'catorcenal' && (
                <label className="campo">
                  <span>¿Desde qué día corre el ciclo?</span>
                  <input type="date" value={nuevo.ciclo_desde || ''}
                         onChange={e => setNuevo({ ...nuevo,
                           ciclo_desde: e.target.value })} />
                  {/* Catorce días no se anclan al calendario como la semana o
                      la quincena: hay que decir desde cuándo corre el ciclo, o
                      no hay forma de saber cuál de las dos semanas toca. Se
                      pone el primer día de un periodo que ya se pagó. */}
                  <span className="ayuda">
                    El primer día de un periodo que usted ya haya facturado.
                    De ahí en adelante van de 14 en 14.
                  </span>
                </label>
              )}
              <label className="campo">
                <span>Tipo</span>
                <select value={nuevo.tipo}
                        onChange={e => setNuevo({ ...nuevo, tipo: e.target.value })}>
                  <option value="mayor">Por mayor</option>
                  <option value="detal">Detal</option>
                </select>
              </label>
              {/* LOS KILOS QUE SE LE REGALAN EN CADA VENTA.
                  
                  «A Víctor se le descuentan 3 kilos por el total: si se le
                  venden 100, se le cobran 97.» Es un acuerdo con esa persona y
                  se pacta una vez, no en cada venta.
                  
                  Baja la CUENTA, no la mercancía: el cliente se lleva los 100
                  kilos. */}
              {/* «POR FACTURA», NO POR VIAJE.

                  Decía «por venta» y así se aplicaba: a VICTOR, que recibe
                  cuatro viajes por semana, le daba 12 kilos en vez de los 3
                  que se pactaron. $234.000 donde iban $58.500.

                  El nombre importa tanto como la cuenta: quien escriba un 3
                  aquí tiene que saber si son 3 por semana o 3 por viaje. */}
              <label className="campo">
                <span>Kilos de descuento por factura</span>
                <input type="number" step="0.1" min="0"
                       value={nuevo.descuento_kilos ?? ''} placeholder="0"
                       onChange={e => setNuevo({ ...nuevo, descuento_kilos: e.target.value })} />
                <small style={{ color: 'var(--tinta-tenue)' }}>
                  Se lleva los kilos completos; se le cobran menos esta cantidad.
                  Se descuenta <strong>una sola vez en cada factura</strong>, no
                  en cada despacho.
                </small>
              </label>
            </div>
            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setNuevo(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>
                {guardando ? 'Guardando…' : '💾 Guardar cliente'}
              </button>
            </div>
          </form>
        </div>
      )}

      {abonando && (
        <div className="bloque">
          <h2>💵 Abono de {abonando.cliente.nombre}</h2>
          <form className="cuerpo" onSubmit={abonar}>
            <div className="aviso">
              Debe <strong>{plata(abonando.cliente.saldo)}</strong>
              {abonando.cliente.ultimo_abono &&
                <> · último abono el {fecha(abonando.cliente.ultimo_abono)}</>}
            </div>
            <div className="campos">
              <label className="campo">
                <span>Monto *</span>
                <input type="number" step="1" className="peso" required autoFocus
                       value={abonando.monto}
                       onChange={e => setAbonando({ ...abonando, monto: e.target.value })} />
              </label>
              <label className="campo">
                <span>Fecha</span>
                <input type="date" value={abonando.fecha}
                       onChange={e => setAbonando({ ...abonando, fecha: e.target.value })} />
              </label>
              <label className="campo">
                <span>Cómo pagó</span>
                <select value={abonando.forma}
                        onChange={e => setAbonando({ ...abonando, forma: e.target.value })}>
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="cheque">Cheque</option>
                </select>
              </label>
            </div>
            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setAbonando(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>
                {guardando ? 'Guardando…' : '💾 Anotar el abono'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default Cartera;
